import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PackagePlus, PackageMinus } from "lucide-react";

const TYPES = { entree: { label: "Entrée", cls: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" }, sortie: { label: "Sortie", cls: "border-red-500/40 text-red-600 dark:text-red-400" }, vente: { label: "Vente", cls: "border-sky-500/40 text-sky-600 dark:text-sky-400" }, ajustement: { label: "Ajustement", cls: "border-amber-500/40 text-amber-600 dark:text-amber-400" } };
const MOTIFS = ["Réapprovisionnement fournisseur", "Retour client", "Inventaire", "Produit périmé", "Casse / perte", "Usage interne", "Autre"];

export default function StockDialog({ produit, canEdit, onClose, onChanged }) {
  const [mouvements, setMouvements] = useState([]);
  const [form, setForm] = useState({ type: "entree", quantite: 1, motif: MOTIFS[0], detail: "" });
  const [busy, setBusy] = useState(false);

  const load = (id) => api.get("/stock/mouvements", { params: { produit_id: id } }).then((r) => setMouvements(r.data)).catch(() => {});
  useEffect(() => { if (produit) { load(produit.id); setForm({ type: "entree", quantite: 1, motif: MOTIFS[0], detail: "" }); } }, [produit]);

  if (!produit) return null;
  const motifFinal = form.detail.trim() ? `${form.motif} — ${form.detail.trim()}` : form.motif;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/produits/${produit.id}/stock`, { type: form.type, quantite: Number(form.quantite), motif: motifFinal });
      toast.success(`${TYPES[form.type].label} enregistrée — stock : ${r.data.produit.stock}`);
      onChanged(r.data.produit);
      load(produit.id);
      setForm({ ...form, quantite: 1, detail: "" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-background border-border text-foreground";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto" data-testid="stock-dialog">
        <DialogHeader>
          <DialogTitle>Stock — {produit.nom} <Badge variant="outline" className="ml-2 border-border text-muted-foreground" data-testid="stock-dialog-current">{produit.stock} en stock</Badge></DialogTitle>
        </DialogHeader>
        {canEdit && (
          <div className="grid grid-cols-2 gap-3 border border-border rounded-xl p-3 bg-background/50">
            <div className="col-span-2 grid grid-cols-2 gap-2">
              <Button type="button" variant={form.type === "entree" ? "default" : "outline"} data-testid="stock-type-entree" onClick={() => setForm({ ...form, type: "entree" })} className={form.type === "entree" ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "border-border"}>
                <PackagePlus className="w-4 h-4 mr-2" /> Entrée
              </Button>
              <Button type="button" variant={form.type === "sortie" ? "default" : "outline"} data-testid="stock-type-sortie" onClick={() => setForm({ ...form, type: "sortie" })} className={form.type === "sortie" ? "bg-red-500 hover:bg-red-600 text-white" : "border-border"}>
                <PackageMinus className="w-4 h-4 mr-2" /> Sortie
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-xs">Quantité</Label>
              <Input type="number" min="1" value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="stock-quantite-input" className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-xs">Motif</Label>
              <Select value={form.motif} onValueChange={(v) => setForm({ ...form, motif: v })}>
                <SelectTrigger data-testid="stock-motif-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">{MOTIFS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-foreground/80 text-xs">Précision (optionnel)</Label>
              <Input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder="N° bon de livraison, fournisseur…" data-testid="stock-detail-input" className={inputCls} />
            </div>
            <Button onClick={save} disabled={busy || Number(form.quantite) < 1} data-testid="stock-save-button" className="col-span-2 bg-emerald-500 hover:bg-emerald-600 text-white">
              Enregistrer le mouvement
            </Button>
          </div>
        )}
        <div className="space-y-1.5" data-testid="stock-mouvements-list">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Historique des mouvements</p>
          {mouvements.length === 0 && <p className="text-sm text-muted-foreground" data-testid="stock-mouvements-empty">Aucun mouvement enregistré</p>}
          <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
            {mouvements.map((m) => (
              <div key={m.id} className="flex items-center gap-3 bg-background border border-border rounded-lg px-3 py-2 text-sm" data-testid={`mouvement-${m.id}`}>
                <Badge variant="outline" className={`${TYPES[m.type]?.cls} shrink-0`}>{TYPES[m.type]?.label || m.type}</Badge>
                <span className={`font-mono font-bold shrink-0 ${m.quantite > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{m.quantite > 0 ? `+${m.quantite}` : m.quantite}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-foreground/90">{m.motif}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtDate(m.created_at)} · {m.user_nom} · stock {m.stock_avant} → {m.stock_apres}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
